import { useEffect, useState } from "react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";
import { useI18n } from "@/lib/i18n";

const VALUES = [
  { en: "Authenticity", fr: "Authenticité", desc_fr: "Recettes héritées, jamais simplifiées.", desc_en: "Inherited recipes, never simplified." },
  { en: "Excellence", fr: "Excellence", desc_fr: "Standards d'une grande maison gastronomique.", desc_en: "Standards of a fine dining house." },
  { en: "Teranga", fr: "Teranga", desc_fr: "L'hospitalité sénégalaise dans chaque détail.", desc_en: "Senegalese hospitality in every detail." },
  { en: "Sustainability", fr: "Durabilité", desc_fr: "Producteurs locaux, circuits courts, zéro plastique.", desc_en: "Local producers, short circuits, zero plastic." },
];

export default function About() {
  const { t, lang } = useI18n();
  const [team, setTeam] = useState([]);
  const [awards, setAwards] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data));
    api.get("/awards").then((r) => setAwards(r.data));
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20" data-testid="about-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-7">
            <div className="eyebrow text-[var(--dsf-orange)] mb-3">{t("about.eyebrow")}</div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-[var(--dd-green)] leading-[1.02] mb-6">
              {lang === "fr" ? "Une maison née de la passion." : "A house born of passion."}
            </h1>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={200}>
            <p className="text-[var(--dsf-text-secondary)] text-lg leading-relaxed">
              {lang === "fr"
                ? "Fondée en 2026 à Dakar, Maison Dakar Street Food est née d'une conviction : la cuisine sénégalaise mérite une place sur la scène internationale, sans rien renier de ses racines."
                : "Founded in 2026 in Dakar, Maison Dakar Street Food was born of a conviction: Senegalese cuisine deserves a place on the international stage, without renouncing its roots."}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Big image */}
      <section className="px-6 md:px-12 lg:px-20 mb-20">
        <Reveal>
          <div className="aspect-[21/9] lg:aspect-[21/7] max-h-[420px] overflow-hidden mx-auto max-w-6xl">
            <img src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=2000" alt="" className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </section>

      {/* Story */}
      <section className="py-16 px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <Reveal className="lg:col-span-4 lg:col-start-2">
            <div className="eyebrow text-[var(--dsf-orange)] mb-3">{lang === "fr" ? "Chapitre 01" : "Chapter 01"}</div>
            <h2 className="font-serif text-4xl text-[var(--dd-green)] leading-tight">{lang === "fr" ? "Du marché à la table." : "From market to table."}</h2>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={150}>
            <div className="space-y-5 text-[var(--dsf-text-secondary)] leading-relaxed text-lg">
              <p>{lang === "fr"
                ? "Tout commence dans les marchés de Sandaga, où notre Cheffe Aminata sélectionne chaque matin les meilleurs produits auprès de partenaires fidèles depuis vingt ans."
                : "It all begins in the Sandaga markets, where our Chef Aminata selects the finest products every morning from partners she's worked with for twenty years."}</p>
              <p>{lang === "fr"
                ? "De la cuisson lente du thiéboudienne aux pastels frits à la minute, chaque plat respecte les codes de la tradition sénégalaise tout en intégrant les techniques de la haute gastronomie."
                : "From slow-cooked thiéboudienne to freshly-fried pastels, every dish respects the codes of Senegalese tradition while incorporating fine dining techniques."}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 dd-bg-bone text-[var(--dd-green)] px-6 md:px-12 lg:px-20" data-testid="values-section">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.32em] text-[var(--dsf-orange)] mb-3">{lang === "fr" ? "Nos valeurs" : "Our values"}</div>
          <h2 className="font-serif text-4xl md:text-5xl mb-12 max-w-2xl">{lang === "fr" ? "Quatre principes guident chaque décision." : "Four principles guide every decision."}</h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {VALUES.map((v, i) => (
            <Reveal key={i} delay={i * 100}>
              <div className="border-l border-black/8 pl-5">
                <div className="font-serif text-3xl font-medium mb-3">0{i + 1}</div>
                <div className="font-serif text-2xl font-semibold mb-2">{lang === "fr" ? v.fr : v.en}</div>
                <p className="text-sm text-white/75">{lang === "fr" ? v.desc_fr : v.desc_en}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Full team */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-20" data-testid="team-full">
        <Reveal>
          <div className="text-center mb-16">
            <div className="eyebrow text-[var(--dsf-orange)] mb-3">{t("team.eyebrow")}</div>
            <h2 className="font-serif text-4xl md:text-6xl text-[var(--dd-green)] leading-[1.05]">{lang === "fr" ? "Les visages de la maison" : "The faces of the house"}</h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 lg:gap-10">
          {team.map((m, i) => (
            <Reveal key={i} delay={i * 80}>
              <div className="group" data-testid={`team-member-${i}`}>
                <div className="aspect-[4/5] overflow-hidden mb-4">
                  <img src={m.image} alt={m.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="font-serif text-2xl font-semibold text-[var(--dd-green)]">{m.name}</div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--dsf-orange)] mt-1 mb-3">{m.role}</div>
                <p className="text-sm text-[var(--dsf-text-secondary)]">{m.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Awards (removed per user request) */}

      <Footer />
    </div>
  );
}
